import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Editor from '@monaco-editor/react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
    Play,
    Plus,
    RotateCcw,
    Trash2,
    Download,
    Upload,
    FileCode2,
    Copy,
    ChevronUp,
    ChevronDown,
    Sparkles,
    AlertCircle,
    Clock,
    Terminal,
    Edit3,
    HelpCircle,
    Cpu,
    Maximize2,
    X,
    Eraser,
    FileSpreadsheet,
    CheckCircle2,
    Send,
    Award,
    Layers,
    Check,
} from 'lucide-react';
import { useStudentTheme } from '@context/StudentThemeContext';
import { useRipple } from '@/hooks/useRipple';
import {
    executePythonCell,
    resetPythonRuntime,
    getPyodideInstance,
    writeVirtualDatasetFile,
    evaluateNotebookAssertions,
} from '@services/notebook/pyodideRunner';
import type {
    CellExecutionOutput,
    NotebookEvaluationSummary,
} from '@services/notebook/pyodideRunner';
import codingStudentService from '@/services/coding/codingStudentService';
import type {
    StudentCodingProblemListItem,
    StudentCodingProblemDetails,
} from '@/services/coding/codingStudentService';
import { NOTEBOOK_TEMPLATES } from './notebookTemplates';
import type { NotebookTemplate } from './notebookTemplates';
import toast from 'react-hot-toast';

export interface NotebookCellState {
    id: string;
    type: 'code' | 'markdown';
    content: string;
    status: 'idle' | 'running' | 'success' | 'error';
    executionCount: number | null;
    executionTimeMs?: number;
    output?: CellExecutionOutput;
    isEditingMarkdown?: boolean;
}

interface CellErrorBoundaryState {
    hasError: boolean;
    errorCount: number;
}

class CellErrorBoundary extends React.Component<
    { children: React.ReactNode; resetKey?: any; fallbackContent?: string; onContentChange?: (val: string) => void },
    CellErrorBoundaryState
> {
    private recoverTimer: any = null;

    constructor(props: any) {
        super(props);
        this.state = { hasError: false, errorCount: 0 };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error: any, info: any) {
        console.warn('[Monaco Cell Error Boundary caught]:', error, info);
        if (this.state.errorCount < 3) {
            this.recoverTimer = setTimeout(() => {
                this.setState((prev) => ({ hasError: false, errorCount: prev.errorCount + 1 }));
            }, 60);
        }
    }

    componentDidUpdate(prevProps: any) {
        if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
            this.setState({ hasError: false, errorCount: 0 });
        }
    }

    componentWillUnmount() {
        if (this.recoverTimer) clearTimeout(this.recoverTimer);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="rounded-xl overflow-hidden border border-slate-700 bg-slate-900 p-3 space-y-2">
                    <div className="flex items-center justify-between text-[11px] text-amber-400 font-semibold">
                        <span>Editor re-aligning...</span>
                        <button
                            type="button"
                            onClick={() => this.setState({ hasError: false, errorCount: 0 })}
                            className="px-2 py-0.5 bg-primary text-white rounded text-[10px] cursor-pointer"
                        >
                            Reset View
                        </button>
                    </div>
                    <textarea
                        value={this.props.fallbackContent || ''}
                        onChange={(e) => this.props.onContentChange?.(e.target.value)}
                        rows={Math.max(3, (this.props.fallbackContent || '').split('\n').length + 1)}
                        className="w-full p-2.5 font-mono text-xs bg-slate-950 text-emerald-400 rounded-lg border border-slate-800 focus:outline-none"
                    />
                </div>
            );
        }
        return this.props.children;
    }
}

interface NotebookCodeEditorProps {
    cellId: string;
    orderIndex: number;
    content: string;
    isDark: boolean;
    onChange: (val: string) => void;
}

const editorOptions = {
    minimap: { enabled: false },
    fontSize: 13,
    lineNumbers: 'on' as const,
    scrollBeyondLastLine: false,
    wordWrap: 'on' as const,
    automaticLayout: true,
    tabSize: 4,
    fontFamily: "'Fira Code', 'Cascadia Code', Menlo, Monaco, Consolas, monospace",
    padding: { top: 8, bottom: 8 },
    quickSuggestions: true,
    scrollbar: {
        alwaysConsumeMouseWheel: false,
    },
};

const NotebookCodeEditor: React.FC<NotebookCodeEditorProps> = React.memo(
    ({ cellId, orderIndex, content, isDark, onChange }) => {
        const lineCount = (content || '').split('\n').length;
        const editorHeight = Math.max(80, Math.min(450, lineCount * 21 + 35));

        return (
            <CellErrorBoundary
                resetKey={`${cellId}-${orderIndex}`}
                fallbackContent={content}
                onContentChange={onChange}
            >
                <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-[#232b45]">
                    <Editor
                        key={`editor-${cellId}-${orderIndex}`}
                        path={`inmemory://model/cell_${cellId}.py`}
                        height={`${editorHeight}px`}
                        language="python"
                        theme={isDark ? 'vs-dark' : 'light'}
                        value={content}
                        onChange={(val) => onChange(val || '')}
                        options={editorOptions}
                        keepCurrentModel={false}
                    />
                </div>
            </CellErrorBoundary>
        );
    },
    (prevProps, nextProps) => {
        return (
            prevProps.cellId === nextProps.cellId &&
            prevProps.orderIndex === nextProps.orderIndex &&
            prevProps.content === nextProps.content &&
            prevProps.isDark === nextProps.isDark
        );
    }
);

const STORAGE_KEY = 'student_portal_colab_notebook_v1';

const defaultInitialCells: NotebookCellState[] = [
    {
        id: 'cell-init-1',
        type: 'markdown',
        content: `# 🧪 Google Colab-style Python Lab
Welcome to your browser-powered **interactive Python environment**!
- ⚡ **Zero VPS Compute**: Powered by WebAssembly (Pyodide).
- 📦 **Pre-loaded with Data Science**: \`numpy\`, \`pandas\`, \`matplotlib\`, \`scikit-learn\`!
- 💡 **Tip**: Press \`Shift + Enter\` to run a cell and advance, or select an assigned ML task from the top bar.`,
        status: 'idle',
        executionCount: null,
    },
    {
        id: 'cell-init-2',
        type: 'code',
        content: `# Step 1: Initialize data and imports
import numpy as np
import pandas as pd
import matplotlib.pyplot as plt

print("🎉 Python WebAssembly Runtime Initialized Successfully!")

# Generate sample dataset
cohort_data = {
    'Student': ['Aarav', 'Diya', 'Rohan', 'Ananya', 'Kabir'],
    'AI Track': ['LLMs', 'Computer Vision', 'Deep Learning', 'LLMs', 'Reinforcement Learning'],
    'Score': [94, 88, 92, 98, 85]
}

df = pd.DataFrame(cohort_data)
# Evaluating DataFrame displays interactive table below:
df`,
        status: 'idle',
        executionCount: null,
    },
    {
        id: 'cell-init-3',
        type: 'code',
        content: `# Step 2: Plot cohort scores using Matplotlib
plt.figure(figsize=(7, 3.5), dpi=120)
plt.bar(df['Student'], df['Score'], color=['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'])
plt.title('AI Fellowship Cohort Assessment Scores', fontsize=12, fontweight='bold')
plt.xlabel('Student Name')
plt.ylabel('Score (%)')
plt.ylim(0, 110)
plt.grid(axis='y', linestyle='--', alpha=0.5)
plt.tight_layout()
plt.show()
`,
        status: 'idle',
        executionCount: null,
    },
];

export const StudentNotebook: React.FC = () => {
    const { theme } = useStudentTheme();
    const isDark = theme === 'dark';
    const ripple = useRipple();
    const queryClient = useQueryClient();
    const [searchParams, setSearchParams] = useSearchParams();

    // Task Management State
    const [selectedTaskId, setSelectedTaskId] = useState<string | null>(
        searchParams.get('taskId') || searchParams.get('slug') || null
    );
    const [activeTaskDetails, setActiveTaskDetails] = useState<StudentCodingProblemDetails | null>(null);
    const [isTaskDescriptionOpen, setIsTaskDescriptionOpen] = useState(true);

    // Notebook State
    const [cells, setCells] = useState<NotebookCellState[]>(() => {
        try {
            const saved = localStorage.getItem(STORAGE_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                if (Array.isArray(parsed) && parsed.length > 0) return parsed;
            }
        } catch (e) {
            console.error('Failed to load notebook from local storage', e);
        }
        return defaultInitialCells;
    });

    const [activeCellId, setActiveCellId] = useState<string | null>(null);
    const [runtimeStatus, setRuntimeStatus] = useState<string>('Ready (WebAssembly)');
    const [isRunningAll, setIsRunningAll] = useState<boolean>(false);
    const [isSubmittingTask, setIsSubmittingTask] = useState<boolean>(false);
    const [showTemplateModal, setShowTemplateModal] = useState<boolean>(false);
    const [showShortcutsModal, setShowShortcutsModal] = useState<boolean>(false);
    const [showTaskSelectorModal, setShowTaskSelectorModal] = useState<boolean>(false);
    const [evaluationSummary, setEvaluationSummary] = useState<NotebookEvaluationSummary | null>(null);
    const [zoomImage, setZoomImage] = useState<string | null>(null);
    const [copiedCellId, setCopiedCellId] = useState<string | null>(null);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const executionCounterRef = useRef<number>(1);

    // Fetch Student's Cohort Tasks
    const { data: problemsRes } = useQuery({
        queryKey: ['student-coding-problems'],
        queryFn: () => codingStudentService.getStudentProblems(),
    });

    const studentProblems: StudentCodingProblemListItem[] = problemsRes?.data?.data || [];
    const notebookTasks = studentProblems.filter(
        (p) => p.problem_type === 'NOTEBOOK' || p.problem_type === 'FUNCTION'
    );

    // Load active task details when selectedTaskId changes
    useEffect(() => {
        if (!selectedTaskId) {
            setActiveTaskDetails(null);
            return;
        }

        const loadTask = async () => {
            try {
                const res = await codingStudentService.getProblemDetails(selectedTaskId);
                const task = res.data.data;
                setActiveTaskDetails(task);

                // If task has starter cells, load them
                if (task.starter_cells && task.starter_cells.length > 0) {
                    const loadedCells: NotebookCellState[] = task.starter_cells.map((sc, idx) => ({
                        id: sc.id || `cell-task-${idx}`,
                        type: (sc.type as any) || 'code',
                        content: sc.content,
                        status: 'idle',
                        executionCount: null,
                    }));
                    setCells(loadedCells);
                } else if (task.starter_code) {
                    setCells([
                        {
                            id: 'cell-task-md',
                            type: 'markdown',
                            content: `## 🎯 ${task.title}\n\n${task.description}`,
                            status: 'idle',
                            executionCount: null,
                        },
                        {
                            id: 'cell-task-code',
                            type: 'code',
                            content: task.starter_code,
                            status: 'idle',
                            executionCount: null,
                        },
                    ]);
                }

                // Ingest attached datasets into Pyodide Virtual Filesystem
                if (task.datasets && task.datasets.length > 0) {
                    await mountTaskDatasets(task);
                }
            } catch (err: any) {
                toast.error(err.response?.data?.message || 'Failed to load task details');
            }
        };

        loadTask();
    }, [selectedTaskId]);

    const mountTaskDatasets = useCallback(async (task: StudentCodingProblemDetails) => {
        if (!task.datasets || task.datasets.length === 0) return;

        const newlyMounted: string[] = [];

        for (const ds of task.datasets) {
            try {
                let dataBuffer: ArrayBuffer | null = null;

                // 1. Try dedicated backend API dataset endpoint
                try {
                    const apiRes = await codingStudentService.getDatasetContent(ds.name);
                    if (apiRes.data && (apiRes.data as any).byteLength !== undefined) {
                        dataBuffer = apiRes.data;
                    }
                } catch (apiErr) {
                    // API route fallback
                }

                // 2. Fallback to direct static fetch if API endpoint did not yield buffer
                if (!dataBuffer) {
                    const apiBase = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5050/api/v1';
                    const backendOrigin = apiBase.replace(/\/api(\/v\d+)?\/?$/, '');
                    let fileUrl = ds.file_path;
                    if (!fileUrl.startsWith('http://') && !fileUrl.startsWith('https://')) {
                        const cleanPath = ds.file_path.startsWith('/') ? ds.file_path : `/${ds.file_path}`;
                        fileUrl = `${backendOrigin}${cleanPath}`;
                    }

                    const resp = await fetch(fileUrl);
                    if (resp.ok) {
                        const buffer = await resp.arrayBuffer();
                        const preview = new TextDecoder().decode(buffer.slice(0, 150)).trim().toLowerCase();
                        if (!preview.startsWith('<!doctype') && !preview.startsWith('<html')) {
                            dataBuffer = buffer;
                        }
                    }
                }

                if (dataBuffer) {
                    await writeVirtualDatasetFile(ds.name, dataBuffer);
                    newlyMounted.push(ds.name);
                } else {
                    console.warn(`Could not retrieve binary data for dataset: ${ds.name}`);
                }
            } catch (err) {
                console.error(`Failed to mount dataset ${ds.name}:`, err);
            }
        }

        // Mount quietly in background without popup toast
    }, []);

    // Save to local storage on edit (when in free scratchpad mode)
    useEffect(() => {
        if (!selectedTaskId) {
            try {
                localStorage.setItem(STORAGE_KEY, JSON.stringify(cells));
            } catch (e) {
                console.error('Failed to save to local storage', e);
            }
        }
    }, [cells, selectedTaskId]);

    // Pre-initialize Pyodide engine
    useEffect(() => {
        getPyodideInstance((msg) => setRuntimeStatus(msg)).catch((err) => {
            console.error('Pyodide initialization error:', err);
            setRuntimeStatus('Runtime Error (Check connection)');
        });
    }, []);

    // Cell Operations
    const updateCellContent = (id: string, newContent: string) => {
        setCells((prev) =>
            prev.map((c) => (c.id === id ? { ...c, content: newContent } : c))
        );
    };

    const addCell = (type: 'code' | 'markdown', afterId?: string) => {
        const newCell: NotebookCellState = {
            id: `cell-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
            type,
            content: type === 'code' ? '# Write Python code here\n' : '### Notes\n',
            status: 'idle',
            executionCount: null,
            isEditingMarkdown: type === 'markdown',
        };

        setCells((prev) => {
            if (!afterId) return [...prev, newCell];
            const index = prev.findIndex((c) => c.id === afterId);
            if (index === -1) return [...prev, newCell];
            const updated = [...prev];
            updated.splice(index + 1, 0, newCell);
            return updated;
        });
        setActiveCellId(newCell.id);
    };

    const deleteCell = (id: string) => {
        if (cells.length <= 1) {
            toast.error('Notebook must contain at least one cell');
            return;
        }
        setCells((prev) => prev.filter((c) => c.id !== id));
        if (activeCellId === id) setActiveCellId(null);
    };

    const moveCell = (id: string, direction: 'up' | 'down') => {
        const index = cells.findIndex((c) => c.id === id);
        if (index === -1) return;
        if (direction === 'up' && index === 0) return;
        if (direction === 'down' && index === cells.length - 1) return;

        const newIndex = direction === 'up' ? index - 1 : index + 1;
        const updated = [...cells];
        const [moved] = updated.splice(index, 1);
        updated.splice(newIndex, 0, moved);
        setCells(updated);
    };

    const clearAllOutputs = () => {
        setCells((prev) =>
            prev.map((c) => ({
                ...c,
                output: undefined,
                status: 'idle',
                executionCount: null,
                executionTimeMs: undefined,
            }))
        );
        toast.success('All outputs cleared');
    };

    const scrollToCellOutput = (id: string) => {
        setTimeout(() => {
            const outputElem = document.getElementById(`cell-output-${id}`);
            const cellElem = document.getElementById(`cell-container-${id}`);
            const target = outputElem || cellElem;
            if (target) {
                target.scrollIntoView({
                    behavior: 'smooth',
                    block: 'nearest',
                });
            }
        }, 80);
    };

    // Execute single code cell
    const runCell = useCallback(
        async (id: string) => {
            const cell = cells.find((c) => c.id === id);
            if (!cell || cell.type !== 'code') return;

            if (activeTaskDetails?.datasets?.length) {
                await mountTaskDatasets(activeTaskDetails);
            }

            setCells((prev) =>
                prev.map((c) =>
                    c.id === id ? { ...c, status: 'running' } : c
                )
            );
            setRuntimeStatus('Executing cell...');

            const count = executionCounterRef.current++;
            const result = await executePythonCell(cell.content, (msg) => {
                setRuntimeStatus(msg);
            });

            setCells((prev) =>
                prev.map((c) =>
                    c.id === id
                        ? {
                              ...c,
                              status: result.error ? 'error' : 'success',
                              output: result,
                              executionCount: count,
                              executionTimeMs: result.executionTimeMs,
                          }
                        : c
                )
            );

            setRuntimeStatus('Ready (WebAssembly)');
            scrollToCellOutput(id);
        },
        [cells, activeTaskDetails, mountTaskDatasets]
    );

    // Run All Cells Sequentially
    const handleRunAll = async () => {
        if (isRunningAll) return;
        setIsRunningAll(true);
        toast('Running all cells in sequence...', { icon: '⏳' });

        if (activeTaskDetails?.datasets?.length) {
            await mountTaskDatasets(activeTaskDetails);
        }

        for (const cell of cells) {
            if (cell.type === 'code') {
                setActiveCellId(cell.id);
                setCells((prev) =>
                    prev.map((c) =>
                        c.id === cell.id ? { ...c, status: 'running' } : c
                    )
                );
                const count = executionCounterRef.current++;
                const result = await executePythonCell(cell.content, (msg) => {
                    setRuntimeStatus(msg);
                });

                setCells((prev) =>
                    prev.map((c) =>
                        c.id === cell.id
                            ? {
                                  ...c,
                                  status: result.error ? 'error' : 'success',
                                  output: result,
                                  executionCount: count,
                                  executionTimeMs: result.executionTimeMs,
                              }
                            : c
                    )
                );

                scrollToCellOutput(cell.id);

                if (result.error) {
                    toast.error(`Stopped on error in cell [${count}]`);
                    break;
                }
            }
        }

        setIsRunningAll(false);
        setRuntimeStatus('Ready (WebAssembly)');
        toast.success('Run all completed');
    };

    // Restart Runtime
    const handleRestart = async () => {
        await resetPythonRuntime();
        executionCounterRef.current = 1;
        clearAllOutputs();
        if (activeTaskDetails) {
            await mountTaskDatasets(activeTaskDetails);
        }
        toast.success('Python runtime restarted and memory cleared');
    };

    // Evaluate & Submit ML Task Solution
    const handleEvaluateAndSubmit = async () => {
        if (!activeTaskDetails) {
            toast.error('No active task selected');
            return;
        }

        try {
            setIsSubmittingTask(true);
            toast('Evaluating notebook assertions...', { icon: '🤖' });

            // 1. Run all code cells in sequence first to ensure full namespace is up-to-date
            for (const cell of cells) {
                if (cell.type === 'code') {
                    await executePythonCell(cell.content);
                }
            }

            // 2. Run assertion evaluation
            const assertions = activeTaskDetails.notebook_assertions || [];
            let summary: NotebookEvaluationSummary;

            if (assertions.length > 0) {
                summary = await evaluateNotebookAssertions(assertions);
            } else {
                summary = {
                    total_points: 100,
                    earned_points: 100,
                    score_percentage: 100,
                    passed_count: 1,
                    total_count: 1,
                    is_passed: true,
                    results: [
                        {
                            name: 'Execution check',
                            status: 'PASSED',
                            points: 100,
                            earned_points: 100,
                            explanation: 'Notebook executed without errors.',
                        },
                    ],
                };
            }

            setEvaluationSummary(summary);

            // 3. Submit solution to backend
            const mergedSourceCode = cells
                .filter((c) => c.type === 'code')
                .map((c) => c.content)
                .join('\n\n# --- Next Cell ---\n');

            const status = summary.score_percentage >= 80 ? 'ACCEPTED' : 'WRONG_ANSWER';

            await codingStudentService.submitNotebookSolution(activeTaskDetails.id, {
                notebook_cells: cells.map((c) => ({ id: c.id, type: c.type, content: c.content })),
                source_code: mergedSourceCode,
                score: summary.score_percentage,
                status,
                passed_tests: summary.passed_count,
                total_tests: summary.total_count,
                assertion_results: summary.results,
            });

            queryClient.invalidateQueries({ queryKey: ['student-coding-problems'] });

            if (summary.is_passed) {
                toast.success(`🎉 Excellent! You passed with a score of ${summary.score_percentage}%!`, {
                    duration: 5000,
                });
            } else {
                toast.error(`Score: ${summary.score_percentage}%. Review failed checks below.`);
            }
        } catch (err: any) {
            toast.error(err.response?.data?.message || 'Failed to submit notebook solution');
        } finally {
            setIsSubmittingTask(false);
        }
    };

    // Insert pandas read dataset snippet into active or new cell
    const handleInsertDatasetSnippet = (datasetName: string) => {
        const snippet = `# Load attached dataset\ndf = pd.read_csv('${datasetName}')\nprint(f"Dataset Shape: {df.shape}")\ndf.head()`;
        addCell('code', activeCellId || undefined);
        setTimeout(() => {
            if (activeCellId) {
                updateCellContent(activeCellId, snippet);
            }
        }, 50);
        toast.success(`Inserted snippet for ${datasetName}`);
    };

    // Apply Starter Template
    const applyTemplate = (template: NotebookTemplate) => {
        const newCells: NotebookCellState[] = template.cells.map((tc, idx) => ({
            id: `cell-tmpl-${Date.now()}-${idx}`,
            type: tc.type,
            content: tc.content,
            status: 'idle',
            executionCount: null,
        }));
        setCells(newCells);
        setShowTemplateModal(false);
        setSelectedTaskId(null);
        setActiveTaskDetails(null);
        setSearchParams({});
        toast.success(`Loaded template: "${template.title}"`);
    };

    // Export .ipynb JSON file
    const exportIpynb = () => {
        const ipynbFormat = {
            cells: cells.map((c) => ({
                cell_type: c.type,
                metadata: {},
                source: c.content.split('\n').map((line, i, arr) => (i < arr.length - 1 ? line + '\n' : line)),
                outputs:
                    c.type === 'code' && c.output
                        ? [
                              ...(c.output.stdout
                                  ? [{ output_type: 'stream', name: 'stdout', text: c.output.stdout.split('\n') }]
                                  : []),
                              ...(c.output.error
                                  ? [{ output_type: 'error', ename: 'Error', evalue: '', traceback: [c.output.error] }]
                                  : []),
                              ...c.output.images.map((img) => ({
                                  output_type: 'display_data',
                                  data: { 'image/png': img },
                                  metadata: {},
                              })),
                          ]
                        : [],
                execution_count: c.executionCount,
            })),
            metadata: {
                language_info: { name: 'python', version: '3.12' },
                colab: { name: 'Student Lab Notebook' },
            },
            nbformat: 4,
            nbformat_minor: 2,
        };

        const blob = new Blob([JSON.stringify(ipynbFormat, null, 2)], {
            type: 'application/json',
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = activeTaskDetails ? `${activeTaskDetails.slug}.ipynb` : 'python_lab_notebook.ipynb';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('Notebook exported as .ipynb');
    };

    // Import .ipynb JSON file
    const handleImportFile = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const parsed = JSON.parse(event.target?.result as string);
                if (parsed.cells && Array.isArray(parsed.cells)) {
                    const importedCells: NotebookCellState[] = parsed.cells.map(
                        (c: any, idx: number) => ({
                            id: `cell-import-${Date.now()}-${idx}`,
                            type: c.cell_type === 'markdown' ? 'markdown' : 'code',
                            content: Array.isArray(c.source) ? c.source.join('') : c.source || '',
                            status: 'idle',
                            executionCount: null,
                        })
                    );
                    setCells(importedCells);
                    toast.success('Notebook imported successfully!');
                } else {
                    toast.error('Invalid Jupyter notebook format.');
                }
            } catch (err) {
                toast.error('Failed to parse notebook JSON file.');
            }
        };
        reader.readAsText(file);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const copyToClipboard = (text: string, cellId: string) => {
        navigator.clipboard.writeText(text);
        setCopiedCellId(cellId);
        setTimeout(() => setCopiedCellId(null), 1500);
        toast.success('Copied output to clipboard');
    };

    return (
        <div className="space-y-4 max-w-7xl mx-auto pb-16">
            {/* Task Selector & Header Bar */}
            <div className="bg-white dark:bg-[#141b2e] border border-border dark:border-[#232b45] rounded-2xl p-4 shadow-soft dark:shadow-xl space-y-3">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                    {/* Title and Runtime Status */}
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-blue-600 to-indigo-600 flex items-center justify-center text-white shadow-soft shrink-0">
                            <Cpu size={20} />
                        </div>
                        <div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <h1 className="text-lg font-bold text-navy dark:text-white tracking-tight">
                                    {activeTaskDetails ? activeTaskDetails.title : 'Python Lab (Jupyter Colab)'}
                                </h1>
                                {activeTaskDetails ? (
                                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 dark:bg-purple-950/70 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800">
                                        Assigned ML Task (Week {activeTaskDetails.week_number || 1})
                                    </span>
                                ) : (
                                    <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 dark:bg-emerald-950/70 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                                        Free Scratchpad
                                    </span>
                                )}
                            </div>
                            <p className="text-xs text-slate-body dark:text-slate-400 mt-0.5 flex items-center gap-2">
                                <span className="inline-block w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                                <span className="font-mono">{runtimeStatus}</span>
                            </p>
                        </div>
                    </div>

                    {/* Task Switcher & Main Actions */}
                    <div className="flex items-center gap-2 flex-wrap">
                        {/* Task Selector Button */}
                        <button
                            onClick={() => setShowTaskSelectorModal(true)}
                            onPointerDown={ripple.onPointerDown}
                            className="ripple-trigger flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-50 dark:bg-[#1a233a] border border-border dark:border-[#2d3a5e] text-navy dark:text-slate-200 text-xs font-semibold hover:border-primary transition-all cursor-pointer shadow-xs"
                        >
                            <Layers size={14} className="text-primary" />
                            {activeTaskDetails ? 'Switch Task' : 'Select Assigned ML Task'}
                        </button>

                        {/* Submit Button for Assigned Tasks */}
                        {activeTaskDetails && (
                            <button
                                onClick={handleEvaluateAndSubmit}
                                disabled={isSubmittingTask}
                                onPointerDown={ripple.onPointerDown}
                                className="ripple-trigger flex items-center gap-1.5 px-4 py-1.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-xs font-bold transition-all shadow-md cursor-pointer disabled:opacity-50"
                            >
                                <Send size={14} />
                                {isSubmittingTask ? 'Evaluating...' : 'Validate & Submit Solution'}
                            </button>
                        )}

                        {/* Run All Button */}
                        <button
                            onClick={handleRunAll}
                            disabled={isRunningAll}
                            onPointerDown={ripple.onPointerDown}
                            className="ripple-trigger flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-primary text-white text-xs font-semibold shadow-soft hover:bg-primary/90 transition-all cursor-pointer disabled:opacity-50"
                        >
                            <Play size={13} fill="currentColor" />
                            {isRunningAll ? 'Running...' : 'Run All'}
                        </button>

                        {/* Restart Runtime */}
                        <button
                            onClick={handleRestart}
                            onPointerDown={ripple.onPointerDown}
                            className="ripple-trigger p-2 rounded-xl bg-white dark:bg-[#141b2e] border border-border dark:border-[#232b45] text-slate-600 dark:text-slate-300 hover:text-navy dark:hover:text-white transition-all shadow-xs cursor-pointer"
                            title="Restart Runtime & Clear Variables"
                        >
                            <RotateCcw size={14} />
                        </button>

                        {/* Templates Button */}
                        <button
                            onClick={() => setShowTemplateModal(true)}
                            onPointerDown={ripple.onPointerDown}
                            className="ripple-trigger flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white dark:bg-[#141b2e] border border-border dark:border-[#232b45] text-navy dark:text-slate-200 text-xs font-semibold hover:border-primary transition-all shadow-xs cursor-pointer"
                        >
                            <Sparkles size={14} className="text-amber-500" />
                            Templates
                        </button>

                        {/* Export & Import */}
                        <button
                            onClick={exportIpynb}
                            onPointerDown={ripple.onPointerDown}
                            className="ripple-trigger p-2 rounded-xl bg-white dark:bg-[#141b2e] border border-border dark:border-[#232b45] text-slate-600 dark:text-slate-300 hover:text-navy dark:hover:text-white transition-all shadow-xs cursor-pointer"
                            title="Export as .ipynb"
                        >
                            <Download size={14} />
                        </button>

                        <button
                            onClick={() => fileInputRef.current?.click()}
                            onPointerDown={ripple.onPointerDown}
                            className="ripple-trigger p-2 rounded-xl bg-white dark:bg-[#141b2e] border border-border dark:border-[#232b45] text-slate-600 dark:text-slate-300 hover:text-navy dark:hover:text-white transition-all shadow-xs cursor-pointer"
                            title="Import .ipynb File"
                        >
                            <Upload size={14} />
                        </button>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".ipynb,.json"
                            onChange={handleImportFile}
                            className="hidden"
                        />

                        {/* Shortcuts Modal Button */}
                        <button
                            onClick={() => setShowShortcutsModal(true)}
                            onPointerDown={ripple.onPointerDown}
                            className="ripple-trigger p-2 rounded-xl bg-white dark:bg-[#141b2e] border border-border dark:border-[#232b45] text-slate-600 dark:text-slate-300 hover:text-navy dark:hover:text-white transition-all shadow-xs cursor-pointer"
                            title="Keyboard Shortcuts"
                        >
                            <HelpCircle size={14} />
                        </button>
                    </div>
                </div>

                {/* Attached Datasets Banner */}
                {activeTaskDetails && activeTaskDetails.datasets && activeTaskDetails.datasets.length > 0 && (
                    <div className="pt-2 border-t border-border/80 dark:border-[#232b45] flex items-center gap-2 flex-wrap text-xs">
                        <span className="font-bold text-slate-500 dark:text-slate-400 flex items-center gap-1">
                            <FileSpreadsheet size={13} className="text-emerald-500" />
                            Attached Datasets:
                        </span>
                        {activeTaskDetails.datasets.map((dataset) => (
                            <div
                                key={dataset.name}
                                className="flex items-center gap-2 px-2.5 py-1 rounded-lg bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800 text-emerald-800 dark:text-emerald-300 font-mono text-[11px]"
                            >
                                <span className="font-bold">{dataset.name}</span>
                                {dataset.size_bytes && (
                                    <span className="text-[10px] text-emerald-600 dark:text-emerald-400">
                                        ({Math.round(dataset.size_bytes / 1024)} KB)
                                    </span>
                                )}
                                <button
                                    onClick={() => handleInsertDatasetSnippet(dataset.name)}
                                    className="px-1.5 py-0.5 rounded bg-white dark:bg-[#141b2e] border border-emerald-300 dark:border-emerald-700 text-[10px] font-sans font-semibold text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 cursor-pointer transition-colors"
                                    title="Insert pd.read_csv snippet"
                                >
                                    + Insert Read Code
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                {/* Problem Statement Drawer for Active Task */}
                {activeTaskDetails && (
                    <div className="rounded-xl border border-slate-200 dark:border-[#232b45] bg-slate-50/50 dark:bg-[#0f172a]/50 overflow-hidden text-xs">
                        <button
                            onClick={() => setIsTaskDescriptionOpen((prev) => !prev)}
                            className="w-full px-3.5 py-2 flex items-center justify-between font-bold text-navy dark:text-white hover:bg-slate-100/50 dark:hover:bg-[#1a233a]/50 transition-colors cursor-pointer"
                        >
                            <span className="flex items-center gap-1.5">
                                <FileCode2 size={14} className="text-primary" />
                                Problem Statement & Objectives
                            </span>
                            {isTaskDescriptionOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                        {isTaskDescriptionOpen && (
                            <div className="p-3.5 border-t border-slate-200 dark:border-[#232b45] colab-markdown text-slate-700 dark:text-slate-300 leading-relaxed">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {activeTaskDetails.description}
                                </ReactMarkdown>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Cell Controls Quick Bar */}
            <div className="flex items-center justify-between px-1">
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => addCell('code')}
                        onPointerDown={ripple.onPointerDown}
                        className="ripple-trigger flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-white dark:bg-[#141b2e] border border-border dark:border-[#232b45] text-navy dark:text-slate-200 text-xs font-semibold hover:border-primary hover:text-primary dark:hover:border-primary dark:hover:text-primary transition-all shadow-xs cursor-pointer"
                    >
                        <Plus size={14} className="text-primary" />
                        + Code Cell
                    </button>
                    <button
                        onClick={() => addCell('markdown')}
                        onPointerDown={ripple.onPointerDown}
                        className="ripple-trigger flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-white dark:bg-[#141b2e] border border-border dark:border-[#232b45] text-navy dark:text-slate-200 text-xs font-semibold hover:border-primary hover:text-primary dark:hover:border-primary dark:hover:text-primary transition-all shadow-xs cursor-pointer"
                    >
                        <Plus size={14} className="text-primary" />
                        + Text / Markdown
                    </button>
                </div>
                <button
                    onClick={clearAllOutputs}
                    onPointerDown={ripple.onPointerDown}
                    className="ripple-trigger flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors cursor-pointer"
                >
                    <Eraser size={12} />
                    Clear all outputs
                </button>
            </div>

            {/* Cell List Container */}
            <div className="space-y-4">
                {cells.map((cell, index) => {
                    const isCode = cell.type === 'code';
                    const isActive = activeCellId === cell.id;

                    return (
                        <div
                            key={cell.id}
                            id={`cell-container-${cell.id}`}
                            onClick={() => setActiveCellId(cell.id)}
                            className={`group relative bg-white dark:bg-[#141b2e] border rounded-2xl shadow-soft dark:shadow-xl transition-all duration-200 ${
                                isActive
                                    ? 'border-primary/80 dark:border-primary ring-2 ring-primary/20 dark:bg-[#172138]'
                                    : 'border-border dark:border-[#232b45] hover:border-slate-300 dark:hover:border-slate-700'
                            }`}
                        >
                            {/* Top Cell Toolbar */}
                            <div className="flex items-center justify-between px-4 py-2 border-b border-border/80 dark:border-[#232b45] bg-slate-50/80 dark:bg-[#0e1424]/90 rounded-t-2xl">
                                {/* Left cell meta indicator */}
                                <div className="flex items-center gap-2.5 text-xs">
                                    {isCode ? (
                                        <span className={`font-mono font-bold text-[11px] px-2 py-0.5 rounded-md border ${
                                            cell.status === 'running'
                                                ? 'bg-amber-50 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 border-amber-300 dark:border-amber-700 animate-pulse'
                                                : 'bg-slate-100 dark:bg-[#101828] text-slate-600 dark:text-blue-400 border-slate-200 dark:border-[#232b45]'
                                        }`}>
                                            {cell.status === 'running'
                                                ? '[ * ]'
                                                : cell.executionCount !== null
                                                ? `[ ${cell.executionCount} ]`
                                                : `[   ]`}
                                        </span>
                                    ) : (
                                        <span className="text-[11px] font-semibold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-[#101828] border border-slate-200 dark:border-[#232b45] px-2 py-0.5 rounded-md flex items-center gap-1">
                                            <Edit3 size={11} className="text-primary" />
                                            Markdown
                                        </span>
                                    )}

                                    {cell.executionTimeMs !== undefined && (
                                        <span className="text-[10px] text-slate-400 dark:text-slate-500 flex items-center gap-1 font-mono">
                                            <Clock size={11} />
                                            {cell.executionTimeMs} ms
                                        </span>
                                    )}
                                </div>

                                {/* Right Cell Actions */}
                                <div className="flex items-center gap-1 opacity-90 group-hover:opacity-100 transition-opacity">
                                    {isCode && (
                                        <button
                                            onClick={() => runCell(cell.id)}
                                            disabled={cell.status === 'running'}
                                            onPointerDown={ripple.onPointerDown}
                                            className="ripple-trigger flex items-center gap-1 px-2.5 py-1 bg-primary/10 dark:bg-primary/20 text-primary dark:text-blue-400 hover:bg-primary hover:text-white rounded-lg text-xs font-semibold transition-all cursor-pointer"
                                            title="Run cell (Shift + Enter)"
                                        >
                                            <Play size={11} fill="currentColor" />
                                            Run
                                        </button>
                                    )}

                                    <button
                                        onClick={() => moveCell(cell.id, 'up')}
                                        disabled={index === 0}
                                        className="p-1 rounded-lg hover:bg-slate-200 dark:hover:bg-[#232b45] text-slate-400 hover:text-navy dark:hover:text-white disabled:opacity-30 cursor-pointer"
                                        title="Move Cell Up"
                                    >
                                        <ChevronUp size={14} />
                                    </button>

                                    <button
                                        onClick={() => moveCell(cell.id, 'down')}
                                        disabled={index === cells.length - 1}
                                        className="p-1 rounded-lg hover:bg-slate-200 dark:hover:bg-[#232b45] text-slate-400 hover:text-navy dark:hover:text-white disabled:opacity-30 cursor-pointer"
                                        title="Move Cell Down"
                                    >
                                        <ChevronDown size={14} />
                                    </button>

                                    <button
                                        onClick={() => deleteCell(cell.id)}
                                        className="p-1 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-950/40 text-slate-400 hover:text-rose-600 cursor-pointer"
                                        title="Delete Cell"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>

                            {/* Cell Body */}
                            <div className="p-3">
                                {isCode ? (
                                    <NotebookCodeEditor
                                        cellId={cell.id}
                                        orderIndex={index}
                                        content={cell.content}
                                        isDark={isDark}
                                        onChange={(val) => updateCellContent(cell.id, val)}
                                    />
                                ) : cell.isEditingMarkdown ? (
                                    <div className="space-y-2">
                                        <textarea
                                            value={cell.content}
                                            onChange={(e) => updateCellContent(cell.id, e.target.value)}
                                            rows={Math.max(3, cell.content.split('\n').length + 1)}
                                            className="w-full p-3 font-mono text-xs rounded-xl border border-slate-200 dark:border-[#232b45] bg-slate-50 dark:bg-[#0c1222] text-slate-800 dark:text-slate-100 focus:outline-none focus:border-primary"
                                            placeholder="Type Markdown content here..."
                                        />
                                        <div className="flex justify-end">
                                            <button
                                                onClick={() =>
                                                    setCells((prev) =>
                                                        prev.map((c) =>
                                                            c.id === cell.id ? { ...c, isEditingMarkdown: false } : c
                                                        )
                                                    )
                                                }
                                                className="px-3 py-1 rounded-lg bg-primary text-white text-xs font-semibold cursor-pointer"
                                            >
                                                Done Editing
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div
                                        onDoubleClick={() =>
                                            setCells((prev) =>
                                                prev.map((c) =>
                                                    c.id === cell.id ? { ...c, isEditingMarkdown: true } : c
                                                )
                                            )
                                        }
                                        className="colab-markdown p-2.5 cursor-text rounded-xl hover:bg-slate-50/70 dark:hover:bg-[#1a233a]/40 transition-colors"
                                        title="Double click to edit markdown"
                                    >
                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                            {cell.content || '*Empty markdown cell. Double-click to edit.*'}
                                        </ReactMarkdown>
                                    </div>
                                )}

                                {/* Cell Outputs Section */}
                                {cell.output && (
                                    <div
                                        id={`cell-output-${cell.id}`}
                                        className="mt-3 pt-3 border-t border-slate-200/80 dark:border-[#232b45] space-y-2.5"
                                    >
                                        {/* stdout stream output */}
                                        {cell.output.stdout && (
                                            <div className="p-3.5 bg-[#090d16] border border-slate-800/90 text-emerald-400 font-mono text-xs rounded-xl overflow-x-auto whitespace-pre leading-relaxed shadow-inner">
                                                <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1.5 border-b border-slate-800/80 pb-1">
                                                    <span className="flex items-center gap-1">
                                                        <Terminal size={11} /> stdout
                                                    </span>
                                                    <button
                                                        onClick={() => copyToClipboard(cell.output?.stdout || '', cell.id)}
                                                        className="hover:text-slate-300 transition-colors cursor-pointer flex items-center gap-1"
                                                    >
                                                        <Copy size={10} />
                                                        {copiedCellId === cell.id ? 'Copied!' : 'Copy'}
                                                    </button>
                                                </div>
                                                {cell.output.stdout}
                                            </div>
                                        )}

                                        {/* Error traceback output */}
                                        {cell.output.error && (
                                            <div className="p-3.5 bg-[#1c0c14] border border-rose-900/60 text-rose-200 font-mono text-xs rounded-xl overflow-x-auto whitespace-pre leading-relaxed shadow-inner">
                                                <div className="flex items-center gap-1.5 text-rose-400 font-bold mb-1.5">
                                                    <AlertCircle size={13} />
                                                    Execution Error Traceback:
                                                </div>
                                                {cell.output.error}
                                            </div>
                                        )}

                                        {/* HTML Result (e.g. Pandas DataFrame styled table) */}
                                        {cell.output.htmlResult && (
                                            <div className="overflow-x-auto p-2 bg-white dark:bg-[#0c1222] rounded-xl border border-slate-200 dark:border-[#232b45]">
                                                <div
                                                    className="colab-html-table text-xs text-slate-800 dark:text-slate-200"
                                                    dangerouslySetInnerHTML={{ __html: cell.output.htmlResult }}
                                                />
                                            </div>
                                        )}

                                        {/* Text Expression Result */}
                                        {cell.output.textResult && !cell.output.htmlResult && (
                                            <div className="font-mono text-xs text-slate-700 dark:text-slate-300 px-3 py-2 bg-slate-50 dark:bg-[#0e1424] rounded-lg border border-slate-200 dark:border-[#232b45]">
                                                {cell.output.textResult}
                                            </div>
                                        )}

                                        {/* Matplotlib Graphical Figures */}
                                        {cell.output.images && cell.output.images.length > 0 && (
                                            <div className="space-y-3 pt-1">
                                                {cell.output.images.map((imgBase64, imgIdx) => (
                                                    <div
                                                        key={imgIdx}
                                                        className="relative group/plot inline-block p-2 bg-white rounded-xl border border-slate-200 dark:border-[#232b45] shadow-xs"
                                                    >
                                                        <img
                                                            src={`data:image/png;base64,${imgBase64}`}
                                                            alt={`Matplotlib Plot ${imgIdx + 1}`}
                                                            className="max-w-full rounded-lg"
                                                        />
                                                        <div className="absolute top-3 right-3 opacity-0 group-hover/plot:opacity-100 transition-opacity flex items-center gap-1.5 bg-white/90 dark:bg-[#141b2e]/90 p-1.5 rounded-lg shadow-md border border-slate-200 dark:border-[#232b45]">
                                                            <button
                                                                onClick={() => setZoomImage(imgBase64)}
                                                                className="p-1 text-slate-600 dark:text-slate-300 hover:text-primary transition-colors cursor-pointer"
                                                                title="Zoom plot"
                                                            >
                                                                <Maximize2 size={14} />
                                                            </button>
                                                            <a
                                                                href={`data:image/png;base64,${imgBase64}`}
                                                                download={`plot_${cell.id}_${imgIdx + 1}.png`}
                                                                className="p-1 text-slate-600 dark:text-slate-300 hover:text-primary transition-colors cursor-pointer"
                                                                title="Download plot PNG"
                                                            >
                                                                <Download size={14} />
                                                            </a>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Bottom Add Bar */}
            <div className="flex items-center justify-center gap-3 pt-3">
                <button
                    onClick={() => addCell('code')}
                    onPointerDown={ripple.onPointerDown}
                    className="ripple-trigger flex items-center gap-2 px-4 py-2 rounded-xl bg-primary text-white font-semibold text-xs shadow-soft hover:bg-primary/90 transition-all cursor-pointer"
                >
                    <Plus size={15} />
                    Add Code Cell
                </button>
                <button
                    onClick={() => addCell('markdown')}
                    onPointerDown={ripple.onPointerDown}
                    className="ripple-trigger flex items-center gap-2 px-4 py-2 rounded-xl bg-white dark:bg-[#141b2e] border border-border dark:border-[#232b45] text-slate-700 dark:text-slate-200 font-semibold text-xs shadow-soft hover:bg-slate-50 dark:hover:bg-[#1a233a] transition-all cursor-pointer"
                >
                    <Plus size={15} />
                    Add Text Cell
                </button>
            </div>

            {/* Task Selector Modal */}
            {showTaskSelectorModal && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-[#141b2e] border border-border dark:border-[#232b45] rounded-2xl max-w-2xl w-full p-6 shadow-2xl space-y-4 max-h-[85vh] overflow-y-auto">
                        <div className="flex items-center justify-between pb-3 border-b border-border dark:border-[#232b45]">
                            <div>
                                <h3 className="text-lg font-bold text-navy dark:text-white flex items-center gap-2">
                                    <Layers size={18} className="text-primary" />
                                    Assigned Cohort Tasks
                                </h3>
                                <p className="text-xs text-slate-body dark:text-slate-400">
                                    Select an assigned Machine Learning or coding task to load into your Colab lab.
                                </p>
                            </div>
                            <button
                                onClick={() => setShowTaskSelectorModal(false)}
                                className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-[#1a233a] text-slate-500 cursor-pointer"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {/* Option for Free Scratchpad */}
                        <div
                            onClick={() => {
                                setSelectedTaskId(null);
                                setActiveTaskDetails(null);
                                setSearchParams({});
                                setShowTaskSelectorModal(false);
                                toast.success('Switched to Free Scratchpad');
                            }}
                            className={`p-4 rounded-xl border cursor-pointer transition-all flex items-center justify-between ${
                                !selectedTaskId
                                    ? 'border-primary bg-primary/5 dark:bg-primary/10'
                                    : 'border-border dark:border-[#232b45] hover:border-primary/50'
                            }`}
                        >
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="font-bold text-sm text-navy dark:text-white">🚀 Free Scratchpad</span>
                                    <span className="text-[10px] px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-800 font-semibold">
                                        Open Playground
                                    </span>
                                </div>
                                <p className="text-xs text-slate-500 mt-1">
                                    Unrestricted Python workspace. Create scripts, plot charts, and analyze data freely.
                                </p>
                            </div>
                            {!selectedTaskId && <CheckCircle2 size={18} className="text-primary" />}
                        </div>

                        <div className="space-y-2.5 pt-2">
                            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                                Assigned Tasks for Your Cohort ({notebookTasks.length})
                            </h4>

                            {notebookTasks.length === 0 ? (
                                <p className="text-xs text-slate-500 py-4 text-center">
                                    No assigned tasks yet for your cohort. You can use the Free Scratchpad!
                                </p>
                            ) : (
                                notebookTasks.map((task) => {
                                    const isSelected = selectedTaskId === task.id || selectedTaskId === task.slug;
                                    return (
                                        <div
                                            key={task.id}
                                            onClick={() => {
                                                setSelectedTaskId(task.id);
                                                setSearchParams({ taskId: task.id });
                                                setShowTaskSelectorModal(false);
                                            }}
                                            className={`p-4 rounded-xl border cursor-pointer transition-all flex items-center justify-between ${
                                                isSelected
                                                    ? 'border-primary bg-primary/5 dark:bg-primary/10'
                                                    : 'border-border dark:border-[#232b45] hover:border-primary/50 bg-slate-50/50 dark:bg-[#0e1526]/60'
                                            }`}
                                        >
                                            <div className="space-y-1">
                                                <div className="flex items-center gap-2">
                                                    <span className="font-bold text-sm text-navy dark:text-white">
                                                        {task.title}
                                                    </span>
                                                    {task.problem_type === 'NOTEBOOK' && (
                                                        <span className="text-[10px] px-2 py-0.5 rounded-md bg-purple-100 text-purple-800 font-semibold">
                                                            ML Colab Task
                                                        </span>
                                                    )}
                                                    {task.student_status === 'SOLVED' && (
                                                        <span className="text-[10px] px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-800 font-semibold flex items-center gap-0.5">
                                                            <Check size={10} /> Solved
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-slate-500 line-clamp-1">
                                                    {task.description.replace(/[#*`]/g, '')}
                                                </p>
                                                <div className="flex items-center gap-3 text-[10px] text-slate-400 pt-1">
                                                    <span>Week {task.week_number || 1}</span>
                                                    <span>•</span>
                                                    <span>{task.difficulty}</span>
                                                    {task.datasets_count ? (
                                                        <>
                                                            <span>•</span>
                                                            <span className="text-emerald-600 font-semibold">
                                                                📁 {task.datasets_count} Dataset(s) Attached
                                                            </span>
                                                        </>
                                                    ) : null}
                                                </div>
                                            </div>
                                            {isSelected && <CheckCircle2 size={18} className="text-primary" />}
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Templates Modal */}
            {showTemplateModal && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-[#141b2e] border border-border dark:border-[#232b45] rounded-2xl max-w-2xl w-full p-6 shadow-2xl space-y-4 max-h-[85vh] overflow-y-auto">
                        <div className="flex items-center justify-between pb-3 border-b border-border dark:border-[#232b45]">
                            <div>
                                <h3 className="text-lg font-bold text-navy dark:text-white flex items-center gap-2">
                                    <Sparkles size={18} className="text-primary" />
                                    Sample Python Lab Templates
                                </h3>
                                <p className="text-xs text-slate-body dark:text-slate-400">
                                    Select an interactive notebook template to load pre-configured exercises.
                                </p>
                            </div>
                            <button
                                onClick={() => setShowTemplateModal(false)}
                                className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-[#1a233a] text-slate-500 cursor-pointer"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2">
                            {NOTEBOOK_TEMPLATES.map((tmpl) => (
                                <div
                                    key={tmpl.id}
                                    onClick={() => applyTemplate(tmpl)}
                                    className="group p-4 rounded-xl border border-border dark:border-[#232b45] hover:border-primary dark:hover:border-primary bg-slate-50/50 dark:bg-[#0e1526]/60 hover:bg-blue-50/40 dark:hover:bg-blue-950/20 transition-all cursor-pointer flex flex-col justify-between"
                                >
                                    <div>
                                        <h4 className="font-bold text-sm text-navy dark:text-white group-hover:text-primary transition-colors">
                                            {tmpl.title}
                                        </h4>
                                        <p className="text-xs text-slate-body dark:text-slate-400 mt-1 leading-relaxed">
                                            {tmpl.description}
                                        </p>
                                    </div>
                                    <div className="flex flex-wrap gap-1.5 mt-3">
                                        {tmpl.tags.map((tag) => (
                                            <span
                                                key={tag}
                                                className="text-[10px] px-2 py-0.5 rounded-md bg-white dark:bg-[#141b2e] border border-slate-200 dark:border-[#232b45] text-slate-600 dark:text-slate-300 font-medium"
                                            >
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Submission Evaluation Summary Modal */}
            {evaluationSummary && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-[#141b2e] border border-border dark:border-[#232b45] rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-4">
                        <div className="flex items-center justify-between pb-3 border-b border-border dark:border-[#232b45]">
                            <h3 className="text-base font-bold text-navy dark:text-white flex items-center gap-2">
                                <Award size={20} className="text-primary" />
                                Solution Evaluation Results
                            </h3>
                            <button
                                onClick={() => setEvaluationSummary(null)}
                                className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-[#1a233a] text-slate-500 cursor-pointer"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {/* Score Badge Banner */}
                        <div className={`p-4 rounded-xl text-center space-y-1 ${
                            evaluationSummary.is_passed
                                ? 'bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200 dark:border-emerald-800'
                                : 'bg-amber-50 dark:bg-amber-950/60 border border-amber-200 dark:border-amber-800'
                        }`}>
                            <span className="text-xs font-bold uppercase tracking-wider block text-slate-600 dark:text-slate-300">
                                {evaluationSummary.is_passed ? '🎉 Task Completed Successfully!' : '⚠️ Review Test Requirements'}
                            </span>
                            <div className="text-3xl font-black text-navy dark:text-white">
                                {evaluationSummary.score_percentage}%
                            </div>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                Passed {evaluationSummary.passed_count} of {evaluationSummary.total_count} test assertions ({evaluationSummary.earned_points} / {evaluationSummary.total_points} points)
                            </p>
                        </div>

                        {/* Assertions Breakdown */}
                        <div className="space-y-2 max-h-60 overflow-y-auto">
                            {evaluationSummary.results.map((r, idx) => (
                                <div
                                    key={idx}
                                    className={`p-3 rounded-xl border text-xs ${
                                        r.status === 'PASSED'
                                            ? 'bg-emerald-50/50 dark:bg-emerald-950/20 border-emerald-200 dark:border-emerald-800/60'
                                            : 'bg-rose-50/50 dark:bg-rose-950/20 border-rose-200 dark:border-rose-800/60'
                                    }`}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="font-bold text-navy dark:text-white flex items-center gap-1.5">
                                            {r.status === 'PASSED' ? (
                                                <CheckCircle2 size={14} className="text-emerald-500" />
                                            ) : (
                                                <AlertCircle size={14} className="text-rose-500" />
                                            )}
                                            {r.name}
                                        </span>
                                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                                            r.status === 'PASSED' ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'
                                        }`}>
                                            {r.earned_points} / {r.points} pts
                                        </span>
                                    </div>
                                    {r.error_message && (
                                        <p className="text-[11px] text-rose-600 dark:text-rose-400 font-mono mt-1">
                                            Error: {r.error_message}
                                        </p>
                                    )}
                                    {r.explanation && (
                                        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                                            💡 Hint: {r.explanation}
                                        </p>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="pt-2">
                            <button
                                onClick={() => setEvaluationSummary(null)}
                                className="w-full py-2 bg-primary text-white text-xs font-bold rounded-xl hover:bg-primary/90 cursor-pointer"
                            >
                                Close Summary
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Keyboard Shortcuts Modal */}
            {showShortcutsModal && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
                    <div className="bg-white dark:bg-[#141b2e] border border-border dark:border-[#232b45] rounded-2xl max-w-md w-full p-6 shadow-2xl space-y-4">
                        <div className="flex items-center justify-between pb-2 border-b border-border dark:border-[#232b45]">
                            <h3 className="text-base font-bold text-navy dark:text-white flex items-center gap-2">
                                <HelpCircle size={18} className="text-primary" />
                                Keyboard Shortcuts & Tips
                            </h3>
                            <button
                                onClick={() => setShowShortcutsModal(false)}
                                className="p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-[#1a233a] text-slate-500 cursor-pointer"
                            >
                                <X size={18} />
                            </button>
                        </div>

                        <div className="space-y-2.5 text-xs text-slate-body dark:text-slate-300">
                            <div className="flex justify-between items-center p-2 rounded-lg bg-slate-50 dark:bg-[#0e1526]">
                                <span>Run cell & advance to next</span>
                                <kbd className="px-2 py-1 bg-white dark:bg-[#1a233a] border border-slate-200 dark:border-[#2d3a5e] rounded font-mono font-bold text-[11px] text-navy dark:text-white">
                                    Shift + Enter
                                </kbd>
                            </div>
                            <div className="flex justify-between items-center p-2 rounded-lg bg-slate-50 dark:bg-[#0e1526]">
                                <span>Run cell in place</span>
                                <kbd className="px-2 py-1 bg-white dark:bg-[#1a233a] border border-slate-200 dark:border-[#2d3a5e] rounded font-mono font-bold text-[11px] text-navy dark:text-white">
                                    Ctrl + Enter
                                </kbd>
                            </div>
                            <div className="flex justify-between items-center p-2 rounded-lg bg-slate-50 dark:bg-[#0e1526]">
                                <span>Edit markdown cell</span>
                                <span className="font-semibold text-primary">Double Click</span>
                            </div>
                        </div>

                        <div className="pt-2 text-[11px] text-slate-400 dark:text-slate-500">
                            🔒 <strong>Architecture Note</strong>: Code runs entirely client-side in your browser via WebAssembly (Pyodide). No Python compute is sent to the application VPS.
                        </div>
                    </div>
                </div>
            )}

            {/* Zoom Image Modal */}
            {zoomImage && (
                <div
                    className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={() => setZoomImage(null)}
                >
                    <div className="relative max-w-4xl max-h-[90vh] bg-white dark:bg-[#141b2e] p-3 rounded-2xl shadow-2xl border border-slate-200 dark:border-[#232b45]">
                        <img
                            src={`data:image/png;base64,${zoomImage}`}
                            alt="Zoomed Plot"
                            className="max-w-full max-h-[85vh] object-contain rounded-xl"
                        />
                        <button
                            onClick={() => setZoomImage(null)}
                            className="absolute top-4 right-4 p-2 bg-black/70 hover:bg-black/90 text-white rounded-full cursor-pointer"
                        >
                            <X size={18} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StudentNotebook;
